import { useRef } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import SiteFooter from '../components/SiteFooter.jsx'

gsap.registerPlugin(ScrollTrigger)

const aboutSections = [
  {
    title: 'Focus',
    items: [
      'Systems Design',
      'Application Development',
      'Website Design & Development',
      'Workflow Automation',
      'Information Architecture',
      'UI / UX Design',
    ],
  },
  {
    title: 'Tools & Technologies',
    items: [
      'React',
      'Expo',
      'PostgreSQL',
      'Git',
      'Home Assistant',
      'Docker',
      'Adobe Creative Cloud',
      'HTML / CSS',
      'JavaScript',
      'Google Cloud',
    ],
  },
  {
    title: 'Interests',
    items: [
      'Information Design',
      'Data Visualization',
      'Automation',
      'Connected Systems',
      'Creative Technology',
      'Graphic Design',
      'Illustration',
      'Motion Design',
      'Photography',
    ],
  },
]

export default function AboutPage() {
  const pageRef = useRef(null)

  useGSAP(
    () => {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return
      }

      const introItems = gsap.utils.toArray('[data-about-intro]', pageRef.current)
      const contact = pageRef.current?.querySelector('[data-about-contact]')
      const cards = gsap.utils.toArray('.about-info-card', pageRef.current)

      gsap
        .timeline({ defaults: { ease: 'power3.out' } })
        .fromTo(
          introItems,
          { autoAlpha: 0, x: -34 },
          { autoAlpha: 1, x: 0, duration: 0.82, stagger: 0.1 },
        )
        .fromTo(
          contact,
          { autoAlpha: 0, x: -24 },
          { autoAlpha: 1, x: 0, duration: 0.7 },
          0.48,
        )

      cards.forEach((card) => {
        const heading = card.querySelector('[data-about-card-heading]')
        const items = gsap.utils.toArray('[data-about-card-item]', card)

        gsap
          .timeline({
            scrollTrigger: {
              trigger: card,
              start: 'top 86%',
              once: true,
            },
            defaults: { ease: 'power3.out' },
          })
          .fromTo(
            card,
            { autoAlpha: 0, x: 42 },
            { autoAlpha: 1, x: 0, duration: 0.82 },
          )
          .fromTo(
            heading,
            { yPercent: 115 },
            { yPercent: 0, duration: 0.68 },
            0.1,
          )
          .fromTo(
            items,
            { yPercent: 115 },
            { yPercent: 0, duration: 0.72, stagger: 0.05 },
            0.16,
          )
      })
    },
    { scope: pageRef },
  )

  return (
    <>
      <main id="main-content" ref={pageRef} className="about-page">
        <section className="about-profile" aria-labelledby="about-title">
          <aside className="about-profile__aside">
            <div className="about-profile__intro">
              <span className="eyebrow" data-about-intro>
                Studio profile
              </span>
              <h1 id="about-title" className="about-profile__title" data-about-intro>
                About
              </h1>
              <div className="about-profile__divider" data-about-intro aria-hidden="true" />
              <p className="about-profile__caption" data-about-intro>
                Designing thoughtful systems with a strong visual foundation.
              </p>
            </div>

            <div id="contact" className="about-profile__contact" data-about-contact>
              <span className="card-kicker">Contact</span>
              <a href="mailto:info@edgezpace.com">info@edgezpace.com</a>
            </div>
          </aside>

          <div className="about-profile__content">
            {aboutSections.map((section) => (
              <section className="about-info-card" key={section.title}>
                <h2 className="about-info-card__title">
                  <span data-about-card-heading>{section.title}</span>
                </h2>
                <ul className="about-info-card__list">
                  {section.items.map((item) => (
                    <li key={item}>
                      <span data-about-card-item>{item}</span>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </section>
      </main>

      <SiteFooter note="Thoughtful systems, useful digital tools, and a strong visual foundation." />
    </>
  )
}
